import './working-for-bar.scss';

import { type IDelegation, useDelegation } from '@/authz/delegation';
import SupportBadge from '@/components/reusable/support-badge';
import { useOrganizationContext } from '@/context/organization';

export interface WorkingForBarViewProps {
	delegation: IDelegation;
	/** Shown only when the member is locked to an election. */
	electionName?: string | null;
}

/**
 * The persistent "Working for Ada Eze · Candidate" strip. Pure view, so tests and stories can feed
 * it any delegation without a token.
 *
 * It sits in normal flow under the header (never fixed or absolute), so it pushes content down
 * instead of covering it. Renders nothing for anyone who is not a team member.
 */
export const WorkingForBarView = ({ delegation, electionName }: WorkingForBarViewProps) => {
	if (!delegation.isTeamMember) return null;

	const owner = delegation.ownerName || 'the account owner';
	const election = delegation.electionId ? electionName?.trim() : '';

	return (
		<div
			className='working-for-bar'
			role='region'
			aria-label='Team access'
		>
			<p className='working-for-bar__context'>
				<span className='working-for-bar__lead working-for-bar__lead'>Working for</span>{' '}
				<strong className='working-for-bar__owner working-for-bar__owner'>{owner}</strong>
				{delegation.ownerRole && (
					<>
						<span
							className='working-for-bar__sep working-for-bar__sep'
							aria-hidden='true'
						>
							·
						</span>
						<span className='working-for-bar__role working-for-bar__role'>{delegation.ownerRole}</span>
					</>
				)}
				{election && (
					<>
						<span
							className='working-for-bar__sep working-for-bar__sep'
							aria-hidden='true'
						>
							·
						</span>
						<span
							className='working-for-bar__election working-for-bar__election'
							title={election}
						>
							{election}
						</span>
					</>
				)}
			</p>
			{delegation.supportNumber !== null && (
				<p className='working-for-bar__me'>
					<span className='working-for-bar__lead working-for-bar__lead'>You are</span>
					<SupportBadge supportNumber={delegation.supportNumber} />
				</p>
			)}
		</div>
	);
};

/** The bar wired to the live token and the selected election. */
const WorkingForBar = () => {
	const delegation = useDelegation();
	const organization = useOrganizationContext();

	return (
		<WorkingForBarView
			delegation={delegation}
			electionName={organization.selectedElection.electionName}
		/>
	);
};

export default WorkingForBar;
